
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ApplicationService } from './application/application.service';
import { LoaderService, Loader } from './common/loader/loader.service';
import { TokenService } from './token/token.service';
import { DeviceDetectService } from './common/device-detect/device-detect.service';
import { States } from './application/application';
import { EventDetails } from './common/tracking/aws-tracker';
import { AwsTrackingService } from './common/tracking/aws-tracking.service';

@Injectable()
export class AuthGuardService implements CanActivate {

  count = 0;
  turnOffLoaderForRetry = false;

  constructor(
    private router: Router,
    private applicationService: ApplicationService,
    private tokenService: TokenService,
    private deviceDetectService: DeviceDetectService,
    private loaderService: LoaderService,
    private awsTrackingService: AwsTrackingService
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    window.scrollTo(0, 0);
    const applicationId = this.applicationService.getApplicationId();
    this.deviceDetectService.getDeviceData();
    const routeConfig = this.applicationService.getRouteConfig();
    const isCLPaused = this.tokenService.getClPause();
    const csProduct = this.tokenService.getCrossSellProduct();
    let allowedLocations = [];
    if (this.getToken() && routeConfig) {
      this.loaderService.ShowLoaderSource.next(new Loader(true, ''));
      return this.applicationService.getState(applicationId, state.url).pipe(map(response => {
        response = this.newStatesBasedOnRequiredPerfios(applicationId, response);
        const application = this.applicationService.getApplication();
        response.loan.RequiredBankDetails = true;
        if (response.customer.customerType === 'Superprime' && application && application.CashbackType !== 1) {
          response.loan.RequiredBankDetails = false;
        }
        // Set allowed route to only dashboard if cl pause is enabled and the user doesn't have a decided risk2 state or a approved loan
        if (isCLPaused && !response.customer.riskStage2Status && !application && !csProduct) {
          allowedLocations = ['/dashboard'];
        } else {
          allowedLocations = this.getApplicationAllowedLocations(response, routeConfig);
        }
        if (allowedLocations && allowedLocations.length > 0) {
          if (allowedLocations.indexOf(state.url.split('?')[0]) < 0) {
            if (window.location.pathname.split('?')[0] === state.url.split('?')[0]) {
              this.turnOffLoaderForRetry = true;
            }
            if (allowedLocations[0] === this.router.url && this.count < 2) {
              this.count++;
              setTimeout(() => this.router.navigate([state.url]), 1000);
            } else {
              if (this.turnOffLoaderForRetry) {
                this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
              }
              this.checkForSkipPerfiosEvent(applicationId, state.url, response);
              this.turnOffLoaderForRetry = false;
              this.count = 0;
              this.router.navigate([allowedLocations[0]]);
            }
          } else {
            this.turnOffLoaderForRetry = false;
            this.count = 0;
            return true;
          }
        } else {
          this.turnOffLoaderForRetry = false;
          this.count = 0;
          this.router.navigate(['/']);
        }
      }, error => {
        this.turnOffLoaderForRetry = false;
        this.count = 0;
        return false;
      }));
    } else {
      this.turnOffLoaderForRetry = false;
      this.count = 0;
      this.router.navigate(['/']);
    }
  }
  getRoutes(applicationStates?: States): Observable<string[]> {
    const applicationId = this.applicationService.getApplicationId();
    if (!applicationStates) {
      return this.applicationService.getState(applicationId).pipe(map(states => {
        states = this.newStatesBasedOnRequiredPerfios(applicationId, states);
        return this.getAllowedLocations(states);
      }));
    } else {
      applicationStates = this.newStatesBasedOnRequiredPerfios(applicationId, applicationStates);
      const allowedLocation = new Observable<string[]>((observer) => {
        this.getAllowedLocations(applicationStates);
        observer.next();
        observer.complete();
      });
      return allowedLocation;
    }

  }
  getAllowedLocations(states) {
    const routeConfig = this.applicationService.getRouteConfig();
    const application = this.applicationService.getApplication();
    states.loan.RequiredBankDetails = true;
    if (states.customer.customerType === 'Superprime' && application && application.CashbackType !== 1) {
      states.loan.RequiredBankDetails = false;
    }
    const allowedLocations = this.getApplicationAllowedLocations(states, routeConfig);
    return allowedLocations;
  }
  private getApplicationAllowedLocations(states: States, routes: any): string[] {
    let paths = [];
    const kycSkip = this.tokenService.getKycSkip();
    if (kycSkip && !kycSkip.route) {
      if (kycSkip.skip) {
        for (const r of routes) {
          r.conditions = r.conditions.replace('(states.loan.IsPoiUploaded || states.loan.KycFlowCompleted)', '(states.loan.KycFlowCompleted)');
          r.conditions = r.conditions.replace('(!states.loan.IsPoiUploaded || !states.loan.KycFlowCompleted)', '(!states.loan.KycFlowCompleted)');
          r.conditions = r.conditions.replace('(states.customer.proofOfIdUploaded && states.customer.proofOfAddressUpload)', '(states.customer.kycFlowCompleted)');
        }
      } else {
        for (const r of routes) {
          r.conditions = r.conditions.replace('(states.customer.proofOfIdUploaded && states.customer.proofOfAddressUpload)', '((states.customer.proofOfIdUploaded && states.customer.proofOfAddressUpload) || states.customer.kycFlowCompleted)');
        }
      }
      kycSkip.route = true;
      this.tokenService.setKycSkip(kycSkip);
      this.applicationService.updateRouteForKyc(routes);
    }
    if (!states || !states.customer || !states.loan || !routes || routes.length === 0) {
      return paths;
    } else {
      routes.some(route => {
        // tslint:disable-next-line:no-eval
        if (Boolean(eval(route.conditions))) {
          paths = route.paths;
          return true;
        }
      });
      return paths;
    }
  }
  public getToken() {
    return this.tokenService.getToken();
  }
  newStatesBasedOnRequiredPerfios(applicationId, state) {
    let requiredPerfiosFromSession = this.tokenService.getPerfiosFlag();
    const requiredPerfiosFromResponse = applicationId ? state.loan.RequiredPerfios : state.customer.requiredPerfios;
    if (requiredPerfiosFromSession === null || !requiredPerfiosFromResponse) {
      this.tokenService.setPerfiosFlag(requiredPerfiosFromResponse);
      requiredPerfiosFromSession = requiredPerfiosFromResponse;
    }
    if (!requiredPerfiosFromSession) {
      if (applicationId) {
        state.loan.RequiredPerfios = requiredPerfiosFromSession;
      } else {
        state.customer.requiredPerfios = requiredPerfiosFromSession;
      }
    }
    return state;
  }
  checkForSkipPerfiosEvent(applicationId: string, expectedRoute: string, states: States) {
    const requiredPerfiosFromResponse = applicationId ? states.loan.RequiredPerfios : states.customer.requiredPerfios;
    if (expectedRoute.includes('statements') && !requiredPerfiosFromResponse) {
      const event = new EventDetails('income_proof_skipped_risk', 'page', 'skipped');
      this.awsTrackingService.sendDataToFunnel(event).subscribe();
      // this.awsTrackingService.trackMoengageEvent('income_proof_skipped_risk');
    }
  }
}
